/**
 * What a form trigger is allowed to watch.
 *
 * A form trigger's submit becomes `conversion_completed` — the single most
 * important number the product reports ("Primary conversion success",
 * docs/engineering/scoring.md:300). So the selector is not a convenience, it is
 * the filter that decides what counts as a conversion, and it has to be narrow.
 *
 * MEASURED FAILURE (Stag Shop, prod, 2026-09-24). One manually-created trigger
 * with `selector: "form"` matched every form on a Shopify storefront — header
 * search, cart update, footer newsletter, add-to-cart, collection filters — and
 * minted 1,644 conversions in two days against 93 real AI answers. The top
 * "converting" pages were `/cart`, `/a/search` and the homepage.
 *
 * Why the selector and not an id: most real forms carry no `id` at all, so
 * requiring one would fail on the majority of sites. A CSS selector is the
 * stronger primitive — it just has to be QUALIFIED. A bare tag name never is.
 *
 * This is a pure function on purpose, with no Zod: shared is on Zod v3 and the
 * widget on v4, so a schema cannot cross that line but a rule can. One rule,
 * enforced at the API when a trigger is written and again in the widget before
 * it is registered.
 */

/** Selectors that match indiscriminately, whatever else is in the list. */
const CATCH_ALL = new Set(["*", "body", "html", ":root", "form", "div", "section", "main", "article"]);

/** A bare tag name — `form`, `div`, `section`. Matches every one on the page. */
const BARE_TAG = /^[a-z][a-z0-9-]*$/i;

/** The characters that make a selector specific to SOME forms rather than all:
 *  an id, a class, an attribute filter, or a pseudo-class. */
const QUALIFIER = /[#.[:]/;

/** An id anywhere in the chain. An id is unique on a page, so `#wpcf7-f4-p110-o1
 *  form` is scoped to ONE container and is specific even though it ends on a
 *  bare tag — that is the standard Contact Form 7 shape and it is in live use
 *  (bu74ch4r, t52w4reb, jqwqjx61). Rejecting it would have deleted real
 *  conversions. */
const HAS_ID = /#[A-Za-z_-]/;

/** Ids that are LAYOUT, not a form. Every theme has one of these wrapping the
 *  whole page, so `#main form` is `form` with extra steps. Pulled out of the
 *  same instinct as CATCH_ALL: the id is unique, but what it contains is not. */
const LAYOUT_IDS = new Set([
  "main", "content", "page", "wrapper", "site", "root", "app", "body",
  "container", "header", "footer", "primary", "inner", "outer", "shopify-section-header",
]);

/** Ids in a selector, lower-cased and stripped of the leading #. */
function idsIn(part: string): string[] {
  return [...part.matchAll(/#([A-Za-z][\w-]*)/g)].map((m) => (m[1] ?? "").toLowerCase());
}

/** Selectors naming a SEARCH form. A search box submit is not a conversion —
 *  it is the single most common false positive on a storefront, and one tenant
 *  had 14 "conversions" from `#searchForm` (7m5za4r6). Qualified, and still not
 *  a CTA. */
const SEARCHY = /search/i;

export const MAX_SELECTOR_LENGTH = 300;

export type SelectorProblem = {
  /** Stable code, for a typed UI or a test. */
  code: "empty" | "too_long" | "catch_all" | "unqualified" | "search_form";
  /** The offending part of a comma-separated list, for the message. */
  part: string;
  /** Merchant-readable, and specific about the consequence. */
  message: string;
};

/**
 * Why this selector may not be used for a form trigger, or null when it is fine.
 *
 * Every comma-separated part is checked, because one loose part in a list is
 * enough: `#lead-form, form` watches every form on the site.
 */
export function formSelectorProblem(selector: string | null | undefined): SelectorProblem | null {
  const raw = (selector ?? "").trim();
  if (raw.length === 0) {
    return {
      code: "empty",
      part: "",
      message:
        "A form trigger needs a selector that points at one specific form. Without it, every form on the site — search boxes, newsletter signups, carts — would count as a conversion.",
    };
  }
  if (raw.length > MAX_SELECTOR_LENGTH) {
    return { code: "too_long", part: raw.slice(0, 40), message: `A selector may be at most ${MAX_SELECTOR_LENGTH} characters.` };
  }

  for (const rawPart of raw.split(",")) {
    const part = rawPart.trim();
    if (part.length === 0) continue;
    const lowered = part.toLowerCase();

    if (CATCH_ALL.has(lowered)) {
      return {
        code: "catch_all",
        part,
        message: `"${part}" matches every form on the page, so ordinary things — a search box, a newsletter signup, a cart update — would each be recorded as a conversion. Point at one form instead, for example form#contact, .lead-form, or form[action*="contact"].`,
      };
    }

    // `:not(.search)` EXCLUDES search — testing the raw string would refuse a
    // selector written to do exactly the right thing.
    const withoutNegations = lowered.replace(/:not\([^)]*\)/g, "");
    if (SEARCHY.test(withoutNegations)) {
      return {
        code: "search_form",
        part,
        message: `"${part}" looks like a search form. Submitting a search is not a conversion — it is the most common thing a visitor does on a store — so this would inflate your conversion count with people looking around.`,
      };
    }

    // An id anywhere scopes the match to ONE container, so it is specific even
    // when it ends on a bare tag. Otherwise the qualifier has to be on the thing
    // being matched: `#main form` and `div.wrapper form` still match every form
    // in a site-wide container, which on a storefront is all of them.
    // An id scopes the match to one container — unless the id IS the page
    // layout, in which case it contains every form on the site.
    if (HAS_ID.test(part) && !idsIn(part).every((id) => LAYOUT_IDS.has(id))) continue;
    const target = lowered.split(/[\s>+~]+/).filter(Boolean).pop() ?? lowered;
    if (BARE_TAG.test(target) || !QUALIFIER.test(target)) {
      return {
        code: "unqualified",
        part,
        message: `"${part}" is not specific enough — it ends by matching every <${target}> it finds. Add the form's own id, class, or an attribute, for example form#contact, .lead-form, or form[action*="contact"].`,
      };
    }
  }
  return null;
}

/** Convenience for the places that only need a yes/no. */
export function isUsableFormSelector(selector: string | null | undefined): boolean {
  return formSelectorProblem(selector) === null;
}
